"use strict";
/*jshint esversion: 11 */

import React, {useEffect, useRef, useState} from "react";
import {createRoot} from 'react-dom/client';
import PropTypes from "prop-types";
import CsfMembreDossierUfcPrint from './CsfMembreDossierUfcPrint';
import CsfMembreHistoryPrint from './CsfMembreHistoryPrint';
import {
  startLoadingSpinner,
  stopLoadingSpinner
} from "../../csf-ufc-pivotal/includes/utils";
import DrupalAccessTokenApi from "../../../js/csf/DrupalAccessTokenApi";
import CsfMembreDossierUfcDataLayer
  from "../dossier-ufc/CsfMembreDossierUfcDataLayer";
import CsfMemberActivitiesDataLayer from "../csf-membre-activities/CsfMemberActivitiesDataLayer";
import CsfMembreUtils from "../utils/CsfMembreUtils";
import CsfMemberDataLayer from "../utils/CsfMembreDataLayer";

export default function CsfMembrePrintModal({apiUrl, apiEnv, checkedPrintOption = '', setCheckedPrintOption}) {
  const [checkedPrintOptionArray, setCheckedPrintOptionArray] = useState([]);
  const [isError, setIsError] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [member, setMember] = useState({});
  const [dataLayer, setDataLayer] = useState({});
  const [ufcHistoryInfos, setUfcHistoryInfos] = useState([]);
  const [currentPeriodUfcList, setCurrentPeriodUfcList] = useState([]);
  const [previousPeriodUfcList, setPreviousPeriodUfcList] = useState([]);
  const [ufcDataLayer, setUfcDataLayer] = useState({});
  const [currentActivities, setCurrentActivities] = useState([]);
  const [formerActivities, setFormerActivities] = useState([]);
  const [activitiesDataLayer, setActivitiesDataLayer] = useState({});
  const [ufcHistory, setUfcHistory] = useState({});
  const [ufcCase, setUfcCase] = useState({});
  const [renderer, setRenderer] = useState();
  const langCode = document.documentElement.lang;

  // Setup renderer
  useEffect(() => {
    const printWrapper = document.getElementById('member-print-wrapper');

    if (printWrapper !== null) {
      setRenderer(createRoot(printWrapper));
    }
  }, []);

  /*
  * Call this method when an api error occurs.
  * */
  function apiErrorCallback(errorMessage) {
    setIsError(true);
    stopLoadingSpinner();
  }

  useEffect(() => {
    if (checkedPrintOptionArray.length > 0) {
      fetchAccessToken();
    }
  }, [checkedPrintOptionArray]);

  function fetchAccessToken() {
    const drupalAccessTokenApi = new DrupalAccessTokenApi();
    drupalAccessTokenApi.fetchPostRequest()
      .then(response => {
        if (response?.api_access_token) {
          setAccessToken(response.api_access_token);
        }
        else {
          console.error('Error while fetching token');
        }
      })
      .catch(error => {
        console.error(error);
      });
  }

  useEffect(() => {
    if (accessToken) {
      const dataObj = new CsfMembreDossierUfcDataLayer(apiUrl, apiEnv, accessToken, apiErrorCallback);
      dataObj.fetchMemberInfo(setMember);
      setUfcDataLayer(dataObj);
      // Activities
      setDataLayer(new CsfMemberDataLayer(apiUrl, apiEnv, accessToken));
      setActivitiesDataLayer(new CsfMemberActivitiesDataLayer(apiUrl, apiEnv, accessToken));
    }
  }, [accessToken]);

  useEffect(() => {
    if (accessToken && ufcDataLayer?.isInit && member?.Contact_Id) {
      ufcDataLayer.fetchContactInfo(member.Contact_Id, setUfcHistoryInfos);
      ufcDataLayer.fetchCurrentPeriodUfcList(setCurrentPeriodUfcList);
      ufcDataLayer.fetchPreviousPeriodUfcList(setPreviousPeriodUfcList);
    }
  }, [member, ufcDataLayer]);

  function getCurrentPeriodHistoryInfo() {
    startLoadingSpinner();
    // La période courante et précédente sont affichées via le end point CSF_EM_UFC_activities_Info.
    // Les périodes sont en ordre chronologique, le plus petit nombre est par conséquent la période précédente.
    if (ufcHistoryInfos.length > 1) {
      return ufcHistoryInfos[1];
    }
    return null;
  }

  function getPreviousPeriodHistoryInfo() {
    startLoadingSpinner();
    // La période courante et précédente sont affichées via le end point CSF_EM_UFC_activities_Info.
    // Les périodes sont en ordre chronologique, le plus petit nombre est par conséquent la période précédente.
    if (ufcHistoryInfos.length > 0) {
      return ufcHistoryInfos[0];
    }
    return null;
  }

  function getConformityFormatedDate(date) {
    return ufcPeriodDateFormat(date);
  }

  function ufcPeriodDateFormat(dateString) {
    if (dateString == null || dateString === '') {
      return '';
    }

    const date = new Date(dateString);

    const options = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    };

    return new Intl.DateTimeFormat(langCode, options).format(date);
  }

  /* Activities */
  useEffect(() => {
    if(CsfMembreUtils.objectIsEmpty(member) && !CsfMembreUtils.objectIsEmpty(dataLayer)) {
      dataLayer.getMember(setMember);
    }
  }, [dataLayer]);

  useEffect(() => {
    if(!CsfMembreUtils.objectIsEmpty(member) && !CsfMembreUtils.objectIsEmpty(dataLayer)) {
      dataLayer.getMemberUFCCase(member?.Contact_Id, setUfcCase);
    }
  }, [member]);

  useEffect(() => {
    if(!CsfMembreUtils.objectIsEmpty(ufcCase) && !CsfMembreUtils.objectIsEmpty(activitiesDataLayer)) {
      activitiesDataLayer.getCurrentActivities(
        ufcCase?.Education_File__Secondary[0]?.REDU_Education_File_Id,
        setCurrentActivities,
        setIsError
      );

      activitiesDataLayer.getFormerActivities(
        ufcCase?.Education_File__Secondary[0]?.REDU_Education_File_Id,
        setFormerActivities,
        setIsError
      )

      if(!CsfMembreUtils.objectIsEmpty(ufcCase?.Education_File__Secondary[0])) {
        dataLayer.getUfcHistoryInfo(ufcCase?.Education_File__Secondary[0]?.Education_File_Id, setUfcHistory);
      }
    }
  }, [ufcCase]);

  /* Modal */
  useEffect(() => {
    if(checkedPrintOption != '') {
      setCheckedPrintOptionArray([checkedPrintOption]);
    }else {
      setCheckedPrintOptionArray([]);
    }
  }, [checkedPrintOption]);

  function handleCheckedOptions(event){
    let newArray = [];
    if (!event.target.checked) {
      // if checkbox was unchecked, remove it from the array
      newArray = checkedPrintOptionArray.filter((item) => item != event.target.id);
    } else {
      // if checkbox was checked, add it to the array
      newArray = [...checkedPrintOptionArray, event.target.id];
    }
    setCheckedPrintOptionArray(newArray);
  }

  function resetModal() {
    setCheckedPrintOption('');
    setCheckedPrintOptionArray([]);
  }

  function renderSwitch() {
    resetModal();

    renderer.render(
      <>
      { checkedPrintOptionArray.includes('membre_print_summary_current') && (
        <CsfMembreDossierUfcPrint periodTitle={window.Drupal.t('membre.current-period.title')}
                                  periodSubTitle={CsfMembreDossierUfcDataLayer.getPeriodSubtitle(getCurrentPeriodHistoryInfo(), (document?.documentElement?.lang === 'fr'?'fr-FR':'en-US'))}
                                  ufcList={currentPeriodUfcList}
                                  memberName={member["Prénom"] + ' ' + member["Nom"]}
                                  certificatNumber={member["No_Dossier"]}
                                  tableId={"current"}
                                  conformityEndDate={getConformityFormatedDate(getCurrentPeriodHistoryInfo()?.CoursObligatoire_EndDate || '')}
        />
      )}
      { checkedPrintOptionArray.includes('membre_print_summary_previous') && (
        <CsfMembreDossierUfcPrint periodTitle={window.Drupal.t('membre.previous-period.title')}
                                  periodSubTitle={CsfMembreDossierUfcDataLayer.getPeriodSubtitle(getPreviousPeriodHistoryInfo(), (document?.documentElement?.lang === 'fr'?'fr-FR':'en-US'))}
                                  ufcList={previousPeriodUfcList}
                                  memberName={member["Prénom"] + ' ' + member["Nom"]}
                                  certificatNumber={member["No_Dossier"]}
                                  tableId={"previous"}
                                  conformityEndDate={getConformityFormatedDate(getPreviousPeriodHistoryInfo()?.CoursObligatoire_EndDate || '')}
        />
      )}
      { checkedPrintOptionArray.includes('membre_print_activities_current') && (
        <CsfMembreHistoryPrint  periodTitle={window.Drupal.t('membre.current-period.title')}
                                periodSubTitle={dataLayer instanceof CsfMemberDataLayer
                                  ? dataLayer.getUfcPeriodSubtitle(ufcHistory)
                                  : ""}
                                member={member}
                                activities={currentActivities}
                                memberName={member["Prénom"] + ' ' + member["Nom"]}
                                certificatNumber={member["No_Dossier"]}
                                tableId={"current"}
        />
      )}
      { checkedPrintOptionArray.includes('membre_print_activities_previous') && (
        <CsfMembreHistoryPrint  periodTitle={window.Drupal.t('membre.previous-period.title')}
                                periodSubTitle={dataLayer instanceof CsfMemberDataLayer
                                  ? dataLayer.getUfcPeriodSubtitle(ufcHistory, false)
                                  : ""}
                                member={member}
                                activities={formerActivities}
                                memberName={member["Prénom"] + ' ' + member["Nom"]}
                                certificatNumber={member["No_Dossier"]}
                                tableId={"previous"}
        />
    )}
      </>
    );

    // Trigger print only if data minimally initialized and obtained from api
    if (dataLayer?.isInit && member?.Contact_Id && ufcHistoryInfos.length > 0) {
      setTimeout(() => {
        stopLoadingSpinner();
        window.print();
      }, 0); //This gives time to render the print wrapper before printing
    }
  }

  return (
    <div className="modal fade" id="membrePrintModal" tabIndex="-1" role="dialog" aria-labelledby="membrePrintModalTitle" aria-hidden="true" key="">
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header d-flex align-items-center">
            <h2 id="membrePrintModalTitle">{window.Drupal.t('membre.print.modal.title')}</h2>
            <button type="button" className="close" onClick={resetModal} data-dismiss="modal" aria-label={window.Drupal.t('lang.modal.close')}>
              <i className="icon icon-csf-close-noborder"><span className="d-none">&nbsp;</span></i>
            </button>
          </div>

          <div className="modal-body">
            {isError === true && (
              <div className="alert alert-danger mt-1" role="alert"
                aria-live="polite">{window.Drupal.t('broker.manager.error.message')}</div>
            )}
            <fieldset>
              <div className="row align-items-center">
                <legend className="col-12 col-md-8 mt-4 mt-md-0">{window.Drupal.t('membre.dossierUFC.title')}</legend>
                <div className="col-12 col-md-4">
                  <div className="form-group special-padding form-group-checkbox">
                    <input
                        type="checkbox" id="membre_print_summary_current" name="membre_print_summary_current" onChange={handleCheckedOptions} checked={checkedPrintOptionArray.includes('membre_print_summary_current')} />
                    <label htmlFor="membre_print_summary_current">
                      <span className="visually-hidden">{window.Drupal.t('membre.dossierUFC.title')} - </span>{window.Drupal.t('membre.current-period.title')}
                    </label>
                  </div>
                  <div className="form-group special-padding form-group-checkbox">
                    <input
                        type="checkbox" id="membre_print_summary_previous" name="membre_print_summary_previous" onChange={handleCheckedOptions} checked={checkedPrintOptionArray.includes('membre_print_summary_previous')} />
                    <label htmlFor="membre_print_summary_previous">
                      <span className="visually-hidden">{window.Drupal.t('membre.dossierUFC.title')} - </span>{window.Drupal.t('membre.previous-period.title')}
                    </label>
                  </div>
                </div>
              </div>
            </fieldset>
            <hr />
            <fieldset>
              <div className="row align-items-center my-2">
                <legend className="col-12 col-md-8">{window.Drupal.t('membre.activities.title')}</legend>
                <div className="col-12 col-md-4">
                  <div className="form-group special-padding form-group-checkbox">
                    <input
                        type="checkbox" id="membre_print_activities_current" name="membre_print_activities_current" onChange={handleCheckedOptions} checked={checkedPrintOptionArray.includes('membre_print_activities_current')}/>
                    <label htmlFor="membre_print_activities_current">
                      <span className="visually-hidden">{window.Drupal.t('membre.activities.title')} - </span>{window.Drupal.t('membre.current-period.title')}
                    </label>
                  </div>
                  <div className="form-group special-padding form-group-checkbox">
                    <input
                        type="checkbox" id="membre_print_activities_previous" name="membre_print_activities_previous" onChange={handleCheckedOptions} checked={checkedPrintOptionArray.includes('membre_print_activities_previous')} />
                    <label htmlFor="membre_print_activities_previous">
                      <span className="visually-hidden">{window.Drupal.t('membre.activities.title')} - </span>{window.Drupal.t('membre.previous-period.title')}
                    </label>
                  </div>
                </div>
              </div>
            </fieldset>
            <hr />
          </div>

          <div className="modal-footer">
              <div className="space-between-wrapper">
                <button type="button" className='button btn-outlined btn-larger' onClick={resetModal} data-dismiss="modal">{window.Drupal.t('fournisseur.back.btn')}</button>
                <button className="button btn-pale btn-larger" onClick={renderSwitch} data-dismiss="modal" disabled={checkedPrintOptionArray.length == 0}>
                  {window.Drupal.t('fournisseur.gestion.submit.label')}
                </button>
              </div>
          </div>
        </div>
      </div>
    </div>
  )
}

CsfMembrePrintModal.propTypes = {
  apiUrl: PropTypes.string.isRequired,
  apiEnv: PropTypes.string.isRequired,
  checkedPrintOption: PropTypes.string,
  setCheckedPrintOption: PropTypes.func,
};

